<template>
  <div class="page-content gourmet-registration">
    <div class="steps-container">
      <el-steps
        :active="activeStep"
        finish-status="success"
        align-center
      >
        <el-step
          title="Step 1"
          description="Estimate"
        />
        <el-step
          title="Step 2"
          description="Customer Information"
        />
        <el-step
          title="Step 3"
          description="Choose Membership"
        />
        <el-step
          title="Step 4"
          description="Billing Information"
        />
        <el-step
          title="Step 5"
          description="Confirmation"
        />
      </el-steps>
    </div>

    <GourmetEstimate
      v-if="activeStep === GourmetRegistrationSteps.Estimate"
      @goto-next-step="goNextStep"
    />

    <CreateUpdateCustomerInfo
      v-if="activeStep === GourmetRegistrationSteps.CustomerInformation"
      isGourmetRegistration
      @goto-next-step="goNextStep"
    />

    <PricePlanning
      v-if="activeStep === GourmetRegistrationSteps.ChooseMembership"
      isGourmetRegistration
      @goto-next-step="goNextStep"
      @goto-back-step="goBackStep"
    />

    <Payment
      v-if="activeStep === GourmetRegistrationSteps.Payment"
      is-gourmet-registration
      @goto-next-step="goNextStep"
      @goto-back-step="goBackStep"
    />

    <Confirmation
      v-if="activeStep === GourmetRegistrationSteps.Confirmation"
      @init-registration-data="initRegistrationData"
    />

    <div class="col-md-12">
      <div class="form-submit-button-group mb-20">
        <p-button
          v-if="isBackStepAvailable"
          id="btnBack"
          type="warning"
          round
          @click="goBackStep"
        >
          {{ `Back To Step ${activeStep}` }}
        </p-button>

        <p-button
          v-if="isNextStepAvailable"
          id="btnNext"
          type="purple"
          round
          @click="goNextStep"
        >
          Go to Next Step
        </p-button>

        <p-button
          v-if="isSignupLaterAvailable"
          id="btnSignupLater"
          type="danger"
          round
          @click="onSignupLater"
        >
          Sign Up Later
        </p-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { ref, computed, onBeforeUnmount, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useCustomer } from '@/use/customer';
import { useSwalDialog } from '@/use/swalDialog';
import { useAddress } from '@/use/address';
import { lead, leadCaller } from './LeadRegistration/viewModel';

import GourmetEstimate from './Registration/GourmetEstimate.vue';
import CreateUpdateCustomerInfo from './Registration/CreateUpdateCustomer.vue';
import PricePlanning from './Registration/PricePlanning.vue';
import Payment from './Registration/Payment.vue';
import Confirmation from './Registration/GourmetConfirmation.vue';

import { GourmetRegistrationSteps } from '@/models/deliveryOrder';
import { gourmetUsage, gourmetUsageFrequency, gourmetUsageQqqGroup } from '@/services/abTests/abTestsViewModel'

export default {
  name: 'GourmetRegistration',

  components: {
    GourmetEstimate,
    CreateUpdateCustomerInfo,
    PricePlanning,
    Payment,
    Confirmation,
  },

  setup: () => {
    const route = useRoute();
    const router = useRouter();

    const { newCustomerInfo, setNewCustomerInfo, prefillCustomerInfoFromLeadCallerData } = useCustomer();
    const { showConfirmPopupDialogWithValidation } = useSwalDialog();
    const { setRegistrationStartAddress } = useAddress();

    const isFromLeadRegistration = route.query.from === 'lead';

    const activeStep = ref(GourmetRegistrationSteps.Estimate);

    const isBackStepAvailable = computed(() => {
      if (activeStep.value === GourmetRegistrationSteps.Estimate) {
        return false;
      }

      if (activeStep.value === GourmetRegistrationSteps.CustomerInformation && newCustomerInfo.value.id) {
        return false;
      }

      if (activeStep.value === GourmetRegistrationSteps.Confirmation) {
        return false;
      }

      return true;
    });

    const isNextStepAvailable = computed(() => {
      if (activeStep.value === GourmetRegistrationSteps.Estimate) {
        return false;
      }

      if (activeStep.value === GourmetRegistrationSteps.CustomerInformation && !newCustomerInfo.value?.id) {
        return false;
      }

      if (activeStep.value === GourmetRegistrationSteps.ChooseMembership && !newCustomerInfo.value?.registerPlan) {
        return false;
      }

      if (activeStep.value === GourmetRegistrationSteps.Confirmation) {
        return false;
      }

      return true;
    });

    const isSignupLaterAvailable = computed(() => isFromLeadRegistration && activeStep.value === GourmetRegistrationSteps.ChooseMembership);

    const goNextStep = () => {
      activeStep.value++;
    };

    const goBackStep = () => {
      activeStep.value--;
    };

    const onSignupLater = () => {
      router.push({
        name: 'FacebookRegistration',
        query: {
          from: 'registration',
        },
      });
    };

    const initRegistrationData = () => {
      setNewCustomerInfo({
        phoneNumber: '',
      });

      gourmetUsage.value = '';
      gourmetUsageFrequency.value = '';
      gourmetUsageQqqGroup.value = '';

      setRegistrationStartAddress(null);

      activeStep.value = GourmetRegistrationSteps.Estimate;
    };

    const prefillData = () => {
      if (!isFromLeadRegistration) {
        return;
      }

      prefillCustomerInfoFromLeadCallerData(lead.value, leadCaller.value);

      activeStep.value = GourmetRegistrationSteps.CustomerInformation;
    };

    onMounted(prefillData);

    onBeforeUnmount(async () => {
      if (!newCustomerInfo.value.id || activeStep.value === GourmetRegistrationSteps.Confirmation) {
        setNewCustomerInfo({
          phoneNumber: '',
        });

        setRegistrationStartAddress(null);

        return;
      }

      const inputOptions = {
        clearRegistrationData: 'I am done registering the user.',
        keepRegistrationData:
          'I am still on the line with the user and I will need to resume registration after going to a different page.',
      };

      const response = await showConfirmPopupDialogWithValidation({
        text: 'Are you done registering this user or are you still on the line with the user and need to resume registration after going to a different page?',
        input: 'radio',
        validationText: 'Please choose one of the above options',
        inputOptions,
        confirmButtonText: 'Confirm',
      });

      if (response === 'clearRegistrationData') {
        setNewCustomerInfo({
          phoneNumber: '',
        });

        setRegistrationStartAddress(null);
      }
    });

    return {
      GourmetRegistrationSteps,
      activeStep,
      isBackStepAvailable,
      isNextStepAvailable,
      isSignupLaterAvailable,

      goNextStep,
      goBackStep,
      initRegistrationData,
      onSignupLater,
    };
  },
};
</script>
